import { useEffect, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { api, inr, fmtDate, errMsg, img, useSEO } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { useShop } from "../../context/ShopContext";
import ProductCard from "../../components/store/ProductCard";
import { STATES } from "./Checkout";

const inp = "w-full border border-[#E8E0D5] rounded-xl px-4 py-2.5 text-sm bg-white outline-none focus:border-[#6B8E78]";

function Orders() {
  const [orders, setOrders] = useState([]);
  const [ret, setRet] = useState(null);
  const [reason, setReason] = useState("");
  const load = () => api.get("/account/orders").then((r) => setOrders(r.data));
  useEffect(() => { load(); }, []);
  const submit = async () => {
    try {
      await api.post("/account/returns", { order_id: ret, reason });
      toast.success("Return request submitted");
      setRet(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!orders.length) return <p className="text-sm text-stone-500" data-testid="no-orders">No orders yet.</p>;
  return <div className="space-y-4">{orders.map((o) => (
    <div key={o.id} className="border border-[#E8E0D5] rounded-2xl p-5 bg-[#FFFDF9]" data-testid={`account-order-${o.order_number}`}>
      <div className="flex flex-wrap justify-between gap-2"><div><p className="font-medium">{o.order_number}</p><p className="text-xs text-stone-500">{fmtDate(o.created_at, true)}</p></div>
        <div className="text-right"><p className="font-semibold">{inr(o.total)}</p><p className="text-xs"><span className="bg-[#E2EBE2] rounded-full px-2 py-0.5">{o.status}</span> · {o.payment_method === "cod" ? "COD" : o.payment_status}</p></div></div>
      <div className="flex gap-2 mt-3 overflow-x-auto no-scrollbar">{o.items.map((i) => <img key={i.product_id + (i.variant_id || "")} src={img(i.image)} alt={i.name} title={`${i.name} × ${i.qty}`} className="w-12 h-12 rounded-lg object-cover" />)}</div>
      <details className="mt-3 text-sm"><summary className="cursor-pointer text-[#2C523A]">Details & tracking</summary>
        <div className="mt-2 space-y-1 text-stone-600">{o.items.map((i) => <p key={i.product_id + (i.variant_id || "")}>{i.name} × {i.qty} — {inr(i.line_total)}</p>)}
          <p>Deliver to: {o.address.house}, {o.address.street}, {o.address.city}, {o.address.state} {o.address.pincode}</p>
          {o.tracking?.tracking_number && <p>Tracking: {o.tracking.courier} {o.tracking.tracking_number} {o.tracking.tracking_url && <a className="underline" href={o.tracking.tracking_url} target="_blank" rel="noreferrer">Track</a>}</p>}
          <ul className="text-xs pt-1">{o.status_history.map((h, k) => <li key={k}>{fmtDate(h.at, true)} — {h.status}</li>)}</ul>
          {["Delivered", "Shipped"].includes(o.status) && <button onClick={() => setRet(o.id)} className="underline text-xs" data-testid={`return-request-${o.order_number}`}>Request return / refund</button>}
          {ret === o.id && <div className="flex gap-2 pt-2"><input className={inp} placeholder="Reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="return-reason-input" /><button onClick={submit} className="bg-[#1B3B2B] text-white rounded-full px-4 text-xs" data-testid="return-submit-button">Submit</button></div>}
        </div></details>
    </div>
  ))}</div>;
}

function Addresses() {
  const [list, setList] = useState([]);
  const [f, setF] = useState(null);
  const load = () => api.get("/account/addresses").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);
  const save = async (e) => {
    e.preventDefault();
    try { await api.post("/account/addresses", f); setF(null); load(); toast.success("Address saved"); } catch (er) { toast.error(errMsg(er)); }
  };
  return (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">{list.map((a) => (
        <div key={a.id} className="border border-[#E8E0D5] rounded-xl p-4 text-sm bg-[#FFFDF9]"><p className="font-medium">{a.name} · {a.mobile}</p><p className="text-stone-600">{a.house}, {a.street}, {a.city}, {a.state} {a.pincode}</p>
          <button className="text-xs underline text-[#B91C1C] mt-2" onClick={() => api.delete(`/account/addresses/${a.id}`).then(load)} data-testid="address-delete-button">Delete</button></div>
      ))}</div>
      {f ? (
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-3 border border-[#E8E0D5] rounded-2xl p-4" data-testid="address-form">
          {["name", "mobile", "email", "house", "street", "city", "pincode"].map((k) => <input key={k} required placeholder={k[0].toUpperCase() + k.slice(1)} className={inp} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`address-${k}-input`} />)}
          <select required className={inp} value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} data-testid="address-state-select"><option value="">State</option>{STATES.map((s) => <option key={s}>{s}</option>)}</select>
          <button className="bg-[#1B3B2B] text-white rounded-full py-2.5 text-sm" data-testid="address-save-button">Save address</button>
        </form>
      ) : <button onClick={() => setF({ name: "", mobile: "", email: "", house: "", street: "", city: "", state: "", pincode: "" })} className="border border-[#1B3B2B] rounded-full px-5 py-2 text-sm" data-testid="address-add-button">Add address</button>}
    </div>
  );
}

function Profile({ user }) {
  const { checkAuth } = useAuth();
  const [f, setF] = useState({ name: user.name || "", mobile: user.mobile || "" });
  const save = async () => {
    try { await api.put("/account/profile", f); await checkAuth(); toast.success("Profile updated"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-md space-y-3">
      <input className={inp} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="profile-name-input" />
      <input className={inp} placeholder="Mobile" value={f.mobile} onChange={(e) => setF({ ...f, mobile: e.target.value })} data-testid="profile-mobile-input" />
      <p className="text-sm text-stone-500">{user.email}</p>
      <button onClick={save} className="bg-[#1B3B2B] text-white rounded-full px-6 py-2.5 text-sm" data-testid="profile-save-button">Save</button>
    </div>
  );
}

function Wishlist() {
  const { wishlist } = useShop();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/account/wishlist").then((r) => setItems(r.data.items)); }, [wishlist]);
  return items.length ? <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{items.map((p) => <ProductCard key={p.id} p={p} />)}</div> : <p className="text-sm text-stone-500">Your wishlist is empty.</p>;
}

function Returns() {
  const [r, setR] = useState([]);
  useEffect(() => { api.get("/account/returns").then((x) => setR(x.data)); }, []);
  return r.length ? <div className="space-y-2">{r.map((x) => <div key={x.id} className="border border-[#E8E0D5] rounded-xl p-4 text-sm"><b>{x.order_number}</b> · {x.status} · {fmtDate(x.at)}<p className="text-stone-600">{x.reason}</p></div>)}</div> : <p className="text-sm text-stone-500">No return requests.</p>;
}

export default function Account() {
  const { user, logout, isAdmin } = useAuth();
  const [sp, setSp] = useSearchParams();
  useSEO({ title: "My account" });
  if (user === null) return <div className="container-x py-24">Loading…</div>;
  if (!user) return <Navigate to="/login?next=/account" replace />;
  const tab = sp.get("tab") || "orders";
  return (
    <div className="container-x py-12">
      <div className="flex flex-wrap justify-between items-end gap-4 mb-8">
        <div><p className="eyebrow">My account</p><h1 className="text-4xl sm:text-5xl text-[#1B3B2B]">Hello, {user.name?.split(" ")[0]}</h1></div>
        <div className="flex gap-2">{isAdmin && <Link to="/admin" className="rounded-full bg-[#E2EBE2] px-5 py-2 text-sm" data-testid="account-admin-link">Admin Panel</Link>}<button onClick={logout} className="rounded-full border border-[#1B3B2B] px-5 py-2 text-sm" data-testid="logout-button">Log out</button></div>
      </div>
      <Tabs value={tab} onValueChange={(v) => setSp({ tab: v })}>
        <TabsList className="bg-[#F4EFE6] flex-wrap h-auto">{[["orders", "Orders"], ["wishlist", "Wishlist"], ["addresses", "Addresses"], ["returns", "Returns"], ["profile", "Profile"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`account-tab-${v}`}>{l}</TabsTrigger>)}</TabsList>
        <TabsContent value="orders" className="pt-6"><Orders /></TabsContent>
        <TabsContent value="wishlist" className="pt-6"><Wishlist /></TabsContent>
        <TabsContent value="addresses" className="pt-6"><Addresses /></TabsContent>
        <TabsContent value="returns" className="pt-6"><Returns /></TabsContent>
        <TabsContent value="profile" className="pt-6"><Profile user={user} /></TabsContent>
      </Tabs>
    </div>
  );
}
